import React from "react";
import { motion } from "framer-motion";
import { Bot, MessageSquare, FlaskConical, Store, DollarSign, Check, ArrowRight, Play, CheckCircle2, Circle, Loader2 } from "lucide-react";

/* ── Animated simulation graphic ── */
const SimulationGraphic = () => {
  const scenarios = [
    { code: "SC-01", label: "New patient scheduling request", status: "pass" },
    { code: "SC-02", label: "Insurance verification — PPO", status: "pass" },
    { code: "SC-03", label: "Recall patient w/ balance due", status: "pass" },
    { code: "SC-04", label: "Emergency after-hours call", status: "running" },
    { code: "SC-05", label: "Multi-provider availability", status: "pending" },
    { code: "SC-06", label: "Spanish-speaking caller", status: "pending" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.6 }}
      className="w-full rounded-[20px] overflow-hidden border border-slate-200/80 shadow-[0_20px_60px_-16px_rgba(0,0,0,0.1)] bg-white relative"
    >
      {/* Window chrome */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-100">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        </div>
        <span className="text-[10px] font-bold text-slate-400 ml-2 tracking-wide uppercase">Clinician Creator Suite</span>
        <div className="ml-auto flex gap-1">
          {["Scenarios", "Analysis", "Evaluation"].map((tab, i) => (
            <span
              key={tab}
              className={`text-[9px] font-bold px-2.5 py-1 rounded-md ${
                i === 0
                  ? "bg-blue-500 text-white"
                  : "text-slate-400 bg-slate-100"
              }`}
            >
              {tab}
            </span>
          ))}
        </div>
      </div>

      <div className="flex divide-x divide-slate-100">
        {/* Left: scenario list */}
        <div className="w-[55%] p-3">
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Scenarios (6)</span>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
              className="flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 border border-emerald-100"
            >
              <span className="text-[9px] font-bold text-emerald-600">3 passed</span>
            </motion.div>
          </div>

          <div className="space-y-1">
            {scenarios.map((s, i) => (
              <motion.div
                key={s.code}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 + i * 0.1, duration: 0.3 }}
                className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left ${
                  s.status === "running" ? "bg-blue-50/60 border border-blue-100" : "hover:bg-slate-50"
                }`}
              >
                {s.status === "pass" && (
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{ delay: 0.8 + i * 0.15, type: "spring", stiffness: 400 }}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                  </motion.div>
                )}
                {s.status === "running" && (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                  >
                    <Loader2 className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                  </motion.div>
                )}
                {s.status === "pending" && (
                  <Circle className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                )}

                <span className="text-[9px] font-bold text-slate-400 shrink-0 w-[36px]">{s.code}</span>
                <span className="text-[10px] font-semibold text-slate-700 truncate">{s.label}</span>

                {s.status === "pass" && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 1 + i * 0.1 }}
                    className="ml-auto text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded shrink-0"
                  >
                    Pass
                  </motion.span>
                )}
                {s.status === "running" && (
                  <span className="ml-auto text-[9px] font-bold text-blue-500 shrink-0">Running...</span>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right: eval result preview */}
        <div className="w-[45%] p-3">
          <div className="flex items-center gap-1.5 mb-2 px-1">
            <Play className="w-3 h-3 text-blue-500" />
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Eval Result</span>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 1.0, duration: 0.5 }}
            className="rounded-lg bg-slate-50 border border-slate-100 p-3 mb-2"
          >
            <span className="text-[9px] font-bold text-slate-400 block mb-1.5">SC-01 — New patient scheduling</span>
            <div className="space-y-1.5">
              {[
                { label: "Greeting", score: 98 },
                { label: "Info gathering", score: 95 },
                { label: "Slot selection", score: 92 },
                { label: "Confirmation", score: 97 },
              ].map((metric, i) => (
                <motion.div
                  key={metric.label}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: 1.2 + i * 0.1 }}
                  className="flex items-center gap-2"
                >
                  <span className="text-[9px] font-medium text-slate-500 w-[72px] shrink-0">{metric.label}</span>
                  <div className="flex-1 h-[6px] bg-slate-200 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${metric.score}%` }}
                      transition={{ delay: 1.3 + i * 0.12, duration: 0.6, ease: "easeOut" }}
                      className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500"
                    />
                  </div>
                  <span className="text-[9px] font-bold text-emerald-600 w-[24px] text-right">{metric.score}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 5 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6, duration: 0.4 }}
            className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 flex items-center gap-2"
          >
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
            <span className="text-[9px] font-semibold text-emerald-700">All checks passed — ready to publish</span>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

/* ── Main slide ── */
const FellowsClinicianCreatorSlide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-8 xl:p-10 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs */}
      <div
        className="absolute top-[-150px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[140px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #93c5fd 0%, #c4b5fd 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #bfdbfe 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full flex flex-col items-center relative z-10 max-w-[1200px]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h2 className="text-3xl lg:text-[44px] font-extrabold text-slate-900 tracking-tight leading-tight">
            Use our Clinician Creator Suite to{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Launch Your Own AI Agents.
            </span>
          </h2>
          <p className="text-[15px] text-slate-500 font-medium mt-3 max-w-2xl mx-auto">
            Our Creator Suite empowers you to encode your operational and clinical knowledge into AI agents — no code or engineering team required.
          </p>
        </motion.div>

        {/* Two-column: steps + graphic */}
        <div className="w-full flex gap-7 items-start">
          {/* Left: description + revenue */}
          <div className="w-[42%] flex flex-col gap-5 shrink-0">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15, duration: 0.5 }}
              className="rounded-[20px] bg-white/80 border border-white shadow-[0_12px_40px_-12px_rgba(0,0,0,0.06)] backdrop-blur-xl p-6 relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 bottom-0 w-[4px]" style={{ background: "linear-gradient(180deg, #3b82f6, #6366f1, #0ea5e9)" }} />
              <p className="text-[15px] text-slate-700 leading-relaxed font-medium">
                Our suite lets you run <span className="font-bold text-slate-900">thousands of simulations in minutes</span> to benchmark the efficacy of your agent. You can improve it simply by chatting with it — describe what to change in plain English.
              </p>
              <p className="text-[15px] text-slate-700 leading-relaxed font-medium mt-4">
                Once you're ready to publish, our team reviews your agent. After it's listed on the Asha Marketplace, every time another office signs up to use it, you keep <span className="font-extrabold text-emerald-600">70% of the revenue</span>.
              </p>
            </motion.div>

            {/* Revenue highlight */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.4 }}
              className="flex items-center gap-3 p-4 rounded-[16px] bg-emerald-50/80 border border-emerald-100/60"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-md border border-emerald-400 shrink-0">
                <DollarSign className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-[13px] font-bold text-slate-900">Free or paid — you decide</p>
                <p className="text-[11px] text-slate-500 font-medium">
                  Paid agents: you keep <span className="font-extrabold text-emerald-600">70%</span> of revenue. We keep 30%.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Right: animated simulation graphic */}
          <div className="flex-1">
            <SimulationGraphic />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FellowsClinicianCreatorSlide;
